import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import openSocket from "../../services/socket-io";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    let newState = [...state];
    newTickets.forEach((ticket) => {
      const idx = newState.findIndex((t) => t.id === ticket.id);
      if (idx !== -1) {
        newState[idx] = ticket;
        if (ticket.unreadMessages > 0) {
          const [moved] = newState.splice(idx, 1);
          newState.unshift(moved);
        }
      } else {
        newState.push(ticket);
      }
    });
    return newState;
  }
  if (action.type === "RESET_UNREAD") {
    const idx = state.findIndex((t) => t.id === action.payload);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = { ...newState[idx], unreadMessages: 0 };
      return newState;
    }
    return state;
  }
  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const idx = state.findIndex((t) => t.id === ticket.id);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = ticket;
      return newState;
    }
    return [ticket, ...state];
  }
  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    const idx = state.findIndex((t) => t.id === ticket.id);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = ticket;
      newState.unshift(newState.splice(idx, 1)[0]);
      return newState;
    }
    return [ticket, ...state];
  }
  // Like UPDATE_TICKET_UNREAD_MESSAGES but only moves if already in list
  if (action.type === "BUMP_TICKET_IF_PRESENT") {
    const ticket = action.payload;
    const idx = state.findIndex((t) => t.id === ticket.id);
    if (idx === -1) return state;
    const newState = [...state];
    newState[idx] = ticket;
    newState.unshift(newState.splice(idx, 1)[0]);
    return newState;
  }
  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const idx = state.findIndex((t) => t.contactId === contact.id);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = { ...newState[idx], contact };
      return newState;
    }
    return state;
  }
  if (action.type === "DELETE_TICKET") {
    const idx = state.findIndex((t) => t.id === action.payload);
    if (idx !== -1) {
      const newState = [...state];
      newState.splice(idx, 1);
      return newState;
    }
    return state;
  }
  if (action.type === "RESET") return [];
  return state;
};

const TicketsList = ({ status, searchParam, showAll, selectedQueueIds, updateCount, style }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);

  const [count, setCount] = useState(0);
  // Estado efímero "la IA está generando ahora": { [ticketId]: true }. No es
  // parte del ticket ni del reducer; llega por socket (ticketAiTyping) y solo
  // pinta el badge de la tarjeta. `aiTypingTimersRef` guarda un auto-apagado de
  // seguridad por si el evento "off" se pierde en una caída de socket.
  const [aiTyping, setAiTyping] = useState({});
  const aiTypingTimersRef = useRef({});

  const userRef = useRef(user);
  const showAllRef = useRef(showAll);
  const selectedQueueIdsRef = useRef(selectedQueueIds);
  const searchParamRef = useRef(searchParam);
  const ticketsListRef = useRef(ticketsList);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { showAllRef.current = showAll; }, [showAll]);
  useEffect(() => { selectedQueueIdsRef.current = selectedQueueIds; }, [selectedQueueIds]);
  useEffect(() => { searchParamRef.current = searchParam; }, [searchParam]);
  useEffect(() => { ticketsListRef.current = ticketsList; }, [ticketsList]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, showAll, selectedQueueIds]);

  const { tickets, hasMore, loading, count: apiCount } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
  });

  useEffect(() => {
    setCount(apiCount);
  }, [apiCount]);

  useEffect(() => {
    if (!status && !searchParam) return;
    dispatch({ type: "LOAD_TICKETS", payload: tickets });
  }, [tickets]);

  useEffect(() => {
    const socket = openSocket();

    const handleConnect = () => {
      if (status) socket.emit("joinTickets", status);
      else socket.emit("joinNotification");
    };

    const handleTicket = (data) => {
      if (data.action === "updateUnread") {
        dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
      }
      if (data.action === "update") {
        const ticket = data.ticket;
        const exists = ticketsListRef.current.some((t) => t.id === ticket.id);

        // Ticket moved to a different status — remove it from this list
        if (status && ticket.status !== status) {
          if (exists) {
            setCount((prev) => prev - 1);
          }
          dispatch({ type: "DELETE_TICKET", payload: ticket.id });
          return;
        }

        const sp = searchParamRef.current;
        const u = userRef.current;
        const sa = showAllRef.current;
        const sq = selectedQueueIdsRef.current;

        const shouldUpdate = !sp &&
          (!ticket.userId || ticket.userId === u?.id || sa) &&
          (sq.length === 0 || !ticket.queueId || sq.indexOf(ticket.queueId) > -1);

        const notBelongs = ticket.queueId && sq.indexOf(ticket.queueId) === -1;

        if (shouldUpdate) {
          if (!exists) {
            setCount((prev) => prev + 1);
          }
          dispatch({ type: "UPDATE_TICKET", payload: ticket });
        } else if (notBelongs) {
          if (exists) {
            setCount((prev) => prev - 1);
          }
          dispatch({ type: "DELETE_TICKET", payload: ticket.id });
        }
      }
      if (data.action === "delete") {
        const exists = ticketsListRef.current.some((t) => t.id === data.ticketId);
        if (exists) {
          setCount((prev) => prev - 1);
        }
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    };

    const handleAppMessage = (data) => {
      if (!data || !data.action) return;

      const ticket = data.ticket;
      if (!ticket) return;

      // Ignore messages for tickets that don't belong to this status list
      if (status && ticket.status !== status) {
        return;
      }

      const sp = searchParamRef.current;
      const u = userRef.current;
      const sa = showAllRef.current;
      const sq = selectedQueueIdsRef.current;

      const shouldUpdate = !sp &&
        (!ticket.userId || ticket.userId === u?.id || sa) &&
        (sq.length === 0 || !ticket.queueId || sq.indexOf(ticket.queueId) > -1);

      const exists = ticketsListRef.current.some((t) => t.id === ticket.id);

      if (data.action === "create") {
        if (shouldUpdate) {
          if (!exists) {
            setCount((prev) => prev + 1);
          }
          dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: ticket });
        } else {
          // Ticket already visible in list → bring to top even if not "ours"
          dispatch({ type: "BUMP_TICKET_IF_PRESENT", payload: ticket });
        }
      }
      if (data.action === "update" && shouldUpdate) {
        if (!exists) {
          setCount((prev) => prev + 1);
        }
        dispatch({ type: "UPDATE_TICKET", payload: ticket });
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
    };

    const setTicketAiTyping = (ticketId, typing) => {
      const timers = aiTypingTimersRef.current;
      if (timers[ticketId]) {
        clearTimeout(timers[ticketId]);
        delete timers[ticketId];
      }
      setAiTyping((prev) => {
        if (typing) {
          if (prev[ticketId]) return prev;
          return { ...prev, [ticketId]: true };
        }
        if (!prev[ticketId]) return prev;
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      if (typing) {
        // Red de seguridad: si el "off" no llega (socket caído), el badge no se
        // queda encendido para siempre. La llamada al modelo tiene tope de 120s.
        timers[ticketId] = setTimeout(() => {
          delete timers[ticketId];
          setAiTyping((prev) => {
            if (!prev[ticketId]) return prev;
            const next = { ...prev };
            delete next[ticketId];
            return next;
          });
        }, 130000);
      }
    };

    const handleAiTyping = (data) => {
      if (!data || typeof data.ticketId === "undefined") return;
      // Solo pinta tickets visibles en ESTA lista: el enrutado por salas ya
      // filtra por estado, pero el socket es compartido con el chat abierto, así
      // que se confirma la pertenencia para no encender un badge fuera de vista.
      const exists = ticketsListRef.current.some((t) => t.id === data.ticketId);
      if (!exists) return;
      setTicketAiTyping(data.ticketId, !!data.typing);
    };

    socket.on("connect", handleConnect);
    socket.on("reconnect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("appMessage", handleAppMessage);
    socket.on("contact", handleContact);
    socket.on("ticketAiTyping", handleAiTyping);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("reconnect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("appMessage", handleAppMessage);
      socket.off("contact", handleContact);
      socket.off("ticketAiTyping", handleAiTyping);
      Object.values(aiTypingTimersRef.current).forEach(clearTimeout);
      aiTypingTimersRef.current = {};
    };
  }, [status]);

  useEffect(() => {
    if (typeof updateCount === "function") updateCount(count);
  }, [count, updateCount]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      e.currentTarget.scrollTop = scrollTop - 100;
      setPageNumber((p) => p + 1);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={style}>
      <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {ticketsList.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center h-28 mx-10 text-center">
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              {i18n.t("ticketsList.noTicketsTitle")}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {i18n.t("ticketsList.noTicketsMessage")}
            </p>
          </div>
        ) : (
          ticketsList.map((ticket) => (
            <TicketListItem
              ticket={ticket}
              key={ticket.id}
              aiTyping={!!aiTyping[ticket.id]}
            />
          ))
        )}
        {loading && <TicketsListSkeleton />}
      </div>
    </div>
  );
};

export default TicketsList;
